// ═══════════════════════════════════════════════════════════════
// VaultZkContext.js — DEK partagé en mémoire navigateur (Session G)
// ═══════════════════════════════════════════════════════════════
//
// Le DEK déwrappé est un CryptoKey non-extractable conservé UNIQUEMENT
// dans la mémoire React. Pas de sessionStorage, pas de localStorage —
// au refresh / fermeture onglet, il est perdu et le user doit re-saisir
// sa passphrase. C'est volontaire : aucune persistance attaquable XSS.
//
// Utilisation :
//   const { dek, isUnlocked, unlock, lock, challenge, refreshChallenge } = useVaultZk();
// ═══════════════════════════════════════════════════════════════

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { getChallenge } from '../../services/vaultZkApi';

const VaultZkContext = createContext(null);

export function VaultZkProvider({ children }) {
    const [dek, setDek] = useState(null);
    const [challenge, setChallenge] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const refreshChallenge = useCallback(async () => {
        setIsLoading(true);
        try {
            const c = await getChallenge();
            setChallenge(c);
            // Si le serveur dit que ZK n'est plus actif, lock implicite
            if (!c.vault_zk_enabled && dek) setDek(null);
            return c;
        } catch (e) {
            // Auth fail / réseau : on garde l'état actuel
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [dek]);

    const unlock = useCallback((newDek) => {
        setDek(newDek);
    }, []);

    const lock = useCallback(() => {
        setDek(null);
    }, []);

    // Auto-lock si l'onglet passe en arrière-plan plus de 15 min
    useEffect(() => {
        if (!dek) return undefined;
        let hiddenAt = null;
        const onVisibility = () => {
            if (document.hidden) {
                hiddenAt = Date.now();
            } else if (hiddenAt && Date.now() - hiddenAt > 15 * 60 * 1000) {
                setDek(null);
            }
        };
        document.addEventListener('visibilitychange', onVisibility);
        return () => document.removeEventListener('visibilitychange', onVisibility);
    }, [dek]);

    const value = {
        dek,
        isUnlocked: dek !== null,
        unlock,
        lock,
        challenge,
        refreshChallenge,
        isLoading,
    };

    return <VaultZkContext.Provider value={value}>{children}</VaultZkContext.Provider>;
}

export function useVaultZk() {
    const ctx = useContext(VaultZkContext);
    if (!ctx) throw new Error('useVaultZk must be used inside <VaultZkProvider>');
    return ctx;
}
